import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loginHref } from '@/components/auth/login/loginDestination';
import { useAuth } from '@/hooks/useAuth';
import { useRedirectHandler } from '@/hooks/useRedirectHandler';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Global route protection component
 *
 * This component checks if the user is authenticated before allowing access to protected routes.
 * If the user is not authenticated:
 * 1. Stores the current route for redirect after authentication
 * 2. Redirects to the login page (`/login?returnTo=`)
 *
 * Onboarding is not gated here — `OnboardingGate` owns that for every route, public ones included.
 *
 * After successful authentication, the user is automatically redirected to the originally requested route.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { storeRedirect } = useRedirectHandler();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated && !hasRedirectedRef.current) {
      const currentPath = location.pathname + location.search;

      // Store the current route for redirect after authentication
      storeRedirect(currentPath);

      hasRedirectedRef.current = true;
      navigate(loginHref(currentPath), { replace: true });
    } else if (isAuthenticated) {
      // Reset the ref when authenticated so it can redirect again if user logs out
      hasRedirectedRef.current = false;
    }
  }, [
    isAuthenticated,
    location.pathname,
    location.search,
    navigate,
    storeRedirect,
  ]);

  // If not authenticated, return null (redirect is happening)
  if (!isAuthenticated) {
    return null;
  }

  // If authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
